import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const daysOfWeek = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

interface CalendarProps {
  year: number;
  month: number;
  selectedDates: Date[];
  onDateSelect: (date: Date) => void;
  onMonthChange?: (year: number, month: number) => void;
}

const CalendarComponent: React.FC<CalendarProps> = ({
  year,
  month,
  selectedDates,
  onDateSelect,
  onMonthChange,
}) => {
  const getDaysInMonth = (year: number, month: number): number =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number): number =>
    new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfMonth = getFirstDayOfMonth(year, month);

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const paddingDays = Array.from(
    { length: (firstDayOfMonth + 6) % 7 },
    () => null
  );

  const isDateInRange = (day: number): boolean => {
    if (selectedDates.length !== 2) return false;
    const date = new Date(year, month, day);
    return date >= selectedDates[0]! && date <= selectedDates[1]!;
  };

  const isDateSelected = (day: number): boolean => {
    const date = new Date(year, month, day);
    return selectedDates.some(
      (selectedDate) =>
        selectedDate.getFullYear() === date.getFullYear() &&
        selectedDate.getMonth() === date.getMonth() &&
        selectedDate.getDate() === date.getDate()
    );
  };

  const handlePrevMonth = () => {
    const newDate = new Date(year, month - 1);
    onMonthChange?.(newDate.getFullYear(), newDate.getMonth());
  };

  const handleNextMonth = () => {
    const newDate = new Date(year, month + 1);
    onMonthChange?.(newDate.getFullYear(), newDate.getMonth());
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <button className="p-1" onClick={handlePrevMonth}>
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-lg font-semibold">
          {new Date(year, month).toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </h2>
        <button className="p-1" onClick={handleNextMonth}>
          <ChevronRight size={24} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {daysOfWeek.map((day) => (
          <div
            key={day}
            className="text-center text-sm font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
        {[...paddingDays, ...days].map((day, index) => (
          <button
            key={index}
            onClick={() => day && onDateSelect(new Date(year, month, day))}
            className={`
              p-2 text-center text-sm rounded-full
              ${day ? "hover:bg-gray-100" : "invisible"}
              ${isDateSelected(day as number) ? "bg-teal-500 text-white" : ""}
              ${isDateInRange(day as number) ? "bg-teal-100" : ""}
            `}
          >
            {day}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CalendarComponent;
